import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import RoomRateForm from '~/components/Common/Forms/RoomRateForm';
import PermissionComponent from '~/components/Common/PermissionedComponent';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { ROLE_ADMIN_USER, ROLE_COORDINATOR } from '~/consts/roles';

interface RatePlan {
  id: string;
  name: string;
  rate_plan_code?: string | null;
}

interface RoomRate {
  id: string;
  rate_plan: RatePlan;
}

interface Props {
  vendorId: string;
  propertyId: string;
  roomTypeId: string;
  roomRates: Array<RoomRate>;
  roomRate: RoomRate;
  ratePlans: Array<any>;
  propertyIsRental: boolean;
  deleteRoomRate: (roomRateId: string) => void;
  onUpdateRoomRates: (...args: Array<any>) => void;
}

const ROOM_RATE_SEARCH_QUERY_PARAM = 'roomRateSearch';
const SEARCH_INPUT_LABEL = 'Search room rates';
const NO_SEARCH_RESULTS_MESSAGE = 'No room rates match your search.';

function getRoomRateName(roomRate: Pick<RoomRate, 'rate_plan'>) {
  return roomRate.rate_plan.name + (roomRate.rate_plan.rate_plan_code ? ` (${roomRate.rate_plan.rate_plan_code})` : '');
}

export default function RoomTypeRoomRates({
  vendorId,
  propertyId,
  roomTypeId,
  roomRates,
  roomRate,
  ratePlans,
  propertyIsRental,
  deleteRoomRate,
  onUpdateRoomRates,
}: Props) {
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const [roomRateSearch, setRoomRateSearch] = useState('');

  const showConfirmDialog = useConfirmDialog();

  const toggleEditMode = () => {
    setEditing(!editing);
  };

  const toRoomRate = (roomRateId: string) => {
    const searchParams = new URLSearchParams(location.search);

    if (roomRateSearch.trim()) {
      searchParams.set(ROOM_RATE_SEARCH_QUERY_PARAM, roomRateSearch);
    } else {
      searchParams.delete(ROOM_RATE_SEARCH_QUERY_PARAM);
    }

    const queryString = searchParams.toString();

    return {
      pathname: `/vendors/${vendorId}/properties/${propertyId}/room-types/${roomTypeId}/room-rates/${roomRateId}`,
      search: queryString ? `?${queryString}` : '',
    };
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setRoomRateSearch(searchParams.get(ROOM_RATE_SEARCH_QUERY_PARAM) ?? '');
  }, [location.search]);

  const sortedRoomRates = useMemo(() => {
    return [...roomRates].sort((firstRoomRate, secondRoomRate) =>
      getRoomRateName(firstRoomRate).localeCompare(getRoomRateName(secondRoomRate), undefined, {
        sensitivity: 'base',
      }),
    );
  }, [roomRates]);

  const filteredRoomRates = useMemo(() => {
    const trimmedSearch = roomRateSearch.trim().toLowerCase();

    if (!trimmedSearch) {
      return sortedRoomRates;
    }

    return sortedRoomRates.filter((currentRoomRate) => {
      if (currentRoomRate.id === roomRate.id) {
        return true;
      }

      return getRoomRateName(currentRoomRate).toLowerCase().includes(trimmedSearch);
    });
  }, [roomRate.id, roomRateSearch, sortedRoomRates]);

  const handleDelete = async (roomRateId: string) => {
    const confirmed = await showConfirmDialog({
      title: 'Are you sure you want to delete all? This action cannot be undone.',
      description: 'Please delete or re-map packages and packages options with this room rate before deleting.',
    });

    if (!confirmed) return;

    deleteRoomRate(roomRateId);
  };

  const renderSearchInput = () => {
    return (
      <TextField
        label={SEARCH_INPUT_LABEL}
        value={roomRateSearch}
        onChange={(event) => setRoomRateSearch(event.target.value)}
        size="small"
        variant="outlined"
        placeholder="Type to filter rates"
        sx={{ minWidth: 260, maxWidth: 360 }}
      />
    );
  };

  const renderNoResultsMessage = () => {
    if (filteredRoomRates.length > 0) {
      return null;
    }

    return <Alert severity="info">{NO_SEARCH_RESULTS_MESSAGE}</Alert>;
  };

  const renderModal = () => {
    if (!editing) {
      return null;
    }

    return (
      <Dialog open onClose={toggleEditMode} maxWidth="md" fullWidth>
        <DialogTitle>Room rates</DialogTitle>
        <DialogContent>
          <Grid container spacing={0}>
            {sortedRoomRates.map((roomRt) => (
              <Fragment key={roomRt.id}>
                <Grid size={8}>
                  {getRoomRateName(roomRt)}
                  {roomRt.id === roomRate.id ? ' (selected)' : ''}
                </Grid>
                <Grid display="flex" justifyContent="flex-end" alignItems="center" gap={2} size={4}>
                  <Link to={`/vendors/${vendorId}/rate-plans/${roomRt.rate_plan.id}`}>View rate plan</Link>
                  <Divider orientation="vertical" />
                  <Button
                    title="Unlink"
                    color="warning"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(roomRt.id)}
                    disabled={roomRt.id === roomRate.id}
                  >
                    Unlink
                  </Button>
                </Grid>
              </Fragment>
            ))}
          </Grid>
          <RoomRateForm
            propertyId={propertyId}
            roomTypeId={roomTypeId}
            ratePlans={ratePlans}
            onUpdateRoomRates={onUpdateRoomRates}
          />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Stack spacing={2} direction="column">
      <div>
        <Typography className="text-uppercase text-muted">Room Rate</Typography>
      </div>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <Stack spacing={2} flex={1}>
          {renderSearchInput()}
          {renderNoResultsMessage()}
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {filteredRoomRates.map(({ id, ...current }) => (
              <Button
                component={Link}
                key={id}
                to={toRoomRate(id)}
                variant={roomRate.id === id ? 'contained' : 'outlined'}
              >
                {getRoomRateName(current)}
              </Button>
            ))}
          </Stack>
        </Stack>

        {!propertyIsRental && (
          <PermissionComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_COORDINATOR]}>
            <div>
              <Button variant="text" onClick={toggleEditMode}>
                Edit
              </Button>

              {renderModal()}
            </div>
          </PermissionComponent>
        )}
      </Stack>
    </Stack>
  );
}
